// A Group is a wrapper of an input, its label and related message.
(() => {
    class Group extends App.Component {
        constructor(container) {
            super(container);

            this.MESSAGE_STATUS_STATIC = 'static'; // does not disappear on change/focus

            this.fields = [
                'input',
                'select',
                'textarea',
            ];
            this.validator      = { attr: {}, message: '' };
            this.rules          = this.read('rules', false);
            this
                .update()
                .bind()
            ;
        }

        /* Layout */
        get notification() {
            return this.container.querySelector('.message');
        }
        get label() {
            return this.container.querySelector('label');
        }

        /* Getters */
        get name() {
            return this.input.name;
        }

        get level() {
            return this.container.dataset.level;
        }

        /* GETTERS & SETTERS */
        get rules () {
            return this._rules;
        }
        set rules(data) {
            this._rules = [];
            if (data) {
                data.split('-rule-separator-').forEach(rule => {
                    let values      = rule.split('::::');
                    this.rules.push({
                        regex:          values[0],
                        translation:    values[1],
                        type:           values[2],
                        sanitize:       values[3] !== 'false',
                    });
                });
            }
        }

        get value() {
            if (this.isMultiValue()) {
                const values = {};
                this.inputs.forEach(element => {
                    if (element.type !== 'checkbox' || (element.type === 'checkbox' && element.checked)) {
                        values[element.name] = element.value
                    }
                });
                return JSON.stringify(values);
            } else if (this.input.type !== 'checkbox' || (this.input.type === 'checkbox' && this.input.checked)) {
                return this.input.value.trim();
            }

            return '';
        }
        set value(value) {
            if (this.input === null) return this;
            if (this.isMultiValue()) {
                if (!App.Utilities.String.empty(value)) {
                    const values = JSON.parse(value);
                    for (const name in values) {
                        this.container.querySelector('input[name="' + name + '"]').value = values[name];
                    }
                }
                return this;
            }
            switch (this.input.type) {
                case 'radio':
                    this.container.querySelector('input[value="' + value + '"]').checked = true;
                    break;
                case 'checkbox':
                    this.input.checked = value;
                    break;
                case 'select-one':
                    this.input.querySelector('[value="' + value + '"]').selected = true;
                    break;
                case 'select-multiple':
                    this.input.querySelector('[value="' + value + '"]').selected = true;
                    break;
                default:
                    this.input.value = value;
                    this.label.classList.add('active');
            }
            return this;
        }

        /* GETTERS */
        get input() {
            let inputs = this.container.querySelectorAll(this.fields.join(', '));
            switch (inputs.length) {
                case 0:
                    break;
                case 1:
                    return inputs[0];
                default:
                    // In case of multi values, the last input is a
                    // hidden input holding the groups information
                    return this.container.querySelector('input:checked') || inputs[inputs.length -1];
            }
        }
        /* GETTERS */
        get inputs() {
            return this.container.querySelectorAll('input[type="text"], input[type="number"]');
        }
        get required() {
            return this.input.required;
        }
        get type() {
            return this.input.type;
        }

        /**
         * Remove dots, lines and trim string
         * @return {string}
         */
        get sanitize() {
            return this.value.trim().replace(/\.|\-/g, '');
        }

        /* SETTERS */
        set layout(layout) {
            this.container.dataset.layout = layout;
        }
        set message(message) {
            if (this.notification) {
                this.notification.innerHTML = message;
            }
        }

        /* GETTERS AND SETTERS */
        set status(status) {
            this.container.dataset.status = status;
        }
        get status() {
            return this.container.dataset.status;
        }

        /**
         * @param valid
         */
        set valid(valid) {
            if (!this.required) return;
            let classList = this.input.classList;
            if (valid) {
                classList.remove('invalid');
                classList.add('valid');
            } else {
                classList.remove('valid');
                classList.add('invalid');
            }
        }

        validate(input = null) {
            this.removeNotification();
            let value = this.value;

            if (input) {
                value = input.value;
            } else {
                input = this.input;
            }

            input.setCustomValidity('');
            input.checkValidity();

            /* Inline notifications */
            let error   = translation => this.setNotification(__(translation), 'error');
            let alert   = translation => this.setNotification(__(translation), 'alert');

            /* Notifications */
            // If regex rule is not TRUE
            if (value || (!value && !input.validity.valid)) {
                this.rules.forEach(rule => {
                    if (!new RegExp(rule.regex).test(rule.sanitize ? this.sanitize : value) || !value) {
                        this.setNotification(__(rule.translation), rule.type);
                        return this;
                    }
                });
            }
            // if Empty value
            if (this.required && !value) {
                error('FORM.GROUP.MESSAGE.EMPTY');
                return this;
            }
            // if length does not reach min
            if (input.minLength !== -1 && value.length < input.minLength) {
                error('FORM.GROUP.MESSAGE.MINLENGTH');
                return this;
            }
            // if length does not reach max
            if (input.maxLength !== -1 && value.length > input.maxLength) {
                error('FORM.GROUP.MESSAGE.MAXLENGTH');
                return this;
            }
            // if length does not reach min
            if (parseInt(input.min) !== -1 && parseInt(value) < parseInt(input.min)) {
                error('FORM.GROUP.MESSAGE.MIN');
                return this;
            }
            // if length does not reach max
            if (parseInt(input.max) !== -1 && parseInt(value) > parseInt(input.max)) {
                error('FORM.GROUP.MESSAGE.MAX');
                return this;
            }

            /* SUCCESS */
            if (this.required) {
                this.valid = true;
            }
            return this;
        }

        setNotification(message, type) {
            this.status     = type;
            this.message    = message;
            if (!(this.container.closest('.popup-instance')) && this.level === 'high') {
                let Page = new App.Entities.Page();
                Page
                    .setTitle(this.label.innerHTML)
                    .setContent(message)
                ;
                App.Components.Popup.setPage(Page).show();
            }
            switch (type) {
                case 'error':
                    this.track(message);
                    this.valid = false;
                    break;
            }
            return this;
        }

        isMultiValue() {
            return this.inputs.length > 1;
        }

        track(message) {
            if (App.Components.Form.tracks(this.input.form.name)) {
                window.dataLayer.push({
                    'event':            'form-error',
                    'eventCategory':    message,
                    'eventAction':      this.input.form.name || 'No form involved',
                    'eventLabel':       this.name,
                    'eventValue':       (this.value), // eventValue accepts only a boolean
                });
            }
        }

        deny(e) {
            const keycodes = this.input.dataset.deniedKeycodes.split().map((x) => parseInt(x));
            if (keycodes.includes(e.keyCode)) e.preventDefault();
        }

        removeNotification() {
            if (this.status === this.MESSAGE_STATUS_STATIC) return this;
            this.status     = '';
            this.message    = '';
            this.reset();
            return this;
        }

        bind() {
            this.input.addEventListener('focus',    e => this.removeNotification());
            this.input.addEventListener('change',   e => this.removeNotification());
            this.input.addEventListener('blur',     e => setTimeout(() => this.validate(), 100));
            if (this.isMultiValue()) {
                this.inputs.forEach(input => {
                    input.addEventListener('blur',  e => this.validate(input));
                });
            }
            if (this.input.dataset.deniedKeycodes) this.input.addEventListener('keydown',  e => this.deny(e));
            $(this.container).on('click', '.js-input-clear', e => this.empty());
            return this;
        }

        empty() {
            this.value = '';
            let e = $.Event('keyup');
            e.which = 32;
            $(this.input).trigger(e);
            return this;
        }

        reset() {
            this.input.classList.remove('valid');
            this.input.classList.remove('invalid');
        }

        update() {
            return this;
        }
    }
    App.Components.register('.form-group', Group);
})();
